import React, { useState } from 'react';
import { Card, Row, Col, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PackingList from './PackingList';

/**
 * PackingTab component for managing items to pack for a trip
 * 
 * @param {Object} props Component props
 * @param {Object} props.parsedData The parsed plan data
 * @param {Function} props.onSaveChanges Function to save changes to the plan
 * @param {boolean} props.isActive Whether this tab is currently active
 */
function PackingTab({ parsedData, onSaveChanges, isActive = false, readOnly = false }) {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  // Don't auto-save on initialization - just use empty array locally if it doesn't exist
  // This prevents version mismatch errors when just viewing the tab
  
  // Function to handle adding a packing item
  const handleAddItem = (newItem) => {
    // Create a copy of the data
    const updatedData = { ...parsedData };
    
    // Initialize packingItems array if it doesn't exist
    updatedData.packingItems = updatedData.packingItems || [];
    
    // Add the new item
    updatedData.packingItems.push(newItem);
    
    // Save changes
    onSaveChanges(updatedData);
  };
  
  // Function to handle updating a packing item
  const handleChangeItem = (updatedItem) => {
    // Create a deep copy of the data to avoid mutation issues
    const updatedData = { ...parsedData };
    
    // Create a new array copy to ensure immutability
    updatedData.packingItems = (parsedData?.packingItems || []).map(item =>
      item.id === updatedItem.id ? { ...updatedItem } : { ...item }
    );
    
    // Save changes
    onSaveChanges(updatedData);
  };
  
  // Function to handle editing a packing item
  const handleEditItem = (updatedItem) => {
    // Create a copy of the data
    const updatedData = { ...parsedData };
    
    // Initialize packingItems array if it doesn't exist
    updatedData.packingItems = updatedData.packingItems || [];
    
    // Update the item
    updatedData.packingItems = updatedData.packingItems.map(item =>
      item.id === updatedItem.id ? updatedItem : item
    );
    
    // Save changes
    onSaveChanges(updatedData);
  };
  
  // Function to handle deleting a packing item
  const handleDeleteItem = (itemId) => {
    // Create a copy of the data
    const updatedData = { ...parsedData };
    
    // Initialize packingItems array if it doesn't exist
    updatedData.packingItems = updatedData.packingItems || [];
    
    // Filter out the deleted item
    updatedData.packingItems = updatedData.packingItems.filter(item => item.id !== itemId);
    
    // Save changes
    onSaveChanges(updatedData);
  };
  
  // Get all packing items
  const allPackingItems = parsedData?.packingItems || [];
  
  // Category list (original English names for internal use)
  const categories = ["All", "Clothing", "Electronics", "Toiletries", "Documents", "Miscellaneous"];
  
  // Filter items by selected category (same logic as PackingList)
  const packingItems = selectedCategory === "All" 
    ? allPackingItems 
    : allPackingItems.filter(item => item.category === selectedCategory);
  
  // Calculate packing statistics from FILTERED items (matching PackingList)
  const totalItems = packingItems.length;
  const packedItems = packingItems.filter(item => item.status === 'completed').length;
  const packingProgress = totalItems > 0 ? Math.round((packedItems / totalItems) * 100) : 0;
  
  // Group items by category for statistics (use ALL items for category breakdown)
  const itemsByCategory = allPackingItems.reduce((acc, item) => {
    const category = item.category || 'Miscellaneous';
    if (!acc[category]) {
      acc[category] = { total: 0, packed: 0 };
    }
    acc[category].total += 1;
    if (item.status === 'completed') {
      acc[category].packed += 1;
    }
    return acc;
  }, {});
  
  // If tab is not active, show a placeholder message or return null
  if (!isActive) {
    return (
      <div className="text-center p-5">
        <i className="bi bi-suitcase text-muted display-4 mb-3"></i>
        <p className="text-muted">{t('planDetail.packing.willBeLoaded')}</p>
      </div>
    );
  }
  
  return (
    <div className="packing-tab">
      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Card.Title className="mb-4">
            <i className="bi bi-suitcase me-2"></i>
            {t('planDetail.packing.packingList')}
            <span className="text-muted ms-2">
              ({packedItems}/{totalItems} {t('planDetail.packing.itemsPacked')} - {packingProgress}% {t('planDetail.packing.complete')})
            </span>
          </Card.Title>
          
          {/* Packing list statistics */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="bg-light">
                <Card.Body>
                  <h5>{t('planDetail.packing.packingProgress')}</h5>
                  <div className="progress mb-3">
                    <div 
                      className="progress-bar" 
                      role="progressbar" 
                      style={{ width: `${packingProgress}%` }}
                      aria-valuenow={packingProgress} 
                      aria-valuemin="0" 
                      aria-valuemax="100"
                    >
                      {packingProgress}%
                    </div>
                  </div>
                  
                  <Row>
                    {Object.entries(itemsByCategory).map(([category, stats]) => {
                      // Translate category name
                      const categoryKey = category.toLowerCase();
                      const translatedCategory = t(`planDetail.packing.categories.${categoryKey}`, category);
                      return (
                        <Col key={category} xs={6} md={4} lg={3} className="mb-2">
                          <div className="d-flex align-items-center">
                            <span>{translatedCategory}:</span>
                            <span className="text-primary ms-2">
                              {stats.packed}/{stats.total}
                            </span>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          {/* Main packing list */}
          <PackingList
            items={packingItems}
            onItemAdd={readOnly ? undefined : handleAddItem}
            onItemChange={readOnly ? undefined : handleChangeItem}
            onItemDelete={readOnly ? undefined : handleDeleteItem}
            onItemEdit={readOnly ? undefined : handleEditItem}
            category={selectedCategory}
            onCategoryChange={setSelectedCategory}
            categories={categories}
            readOnly={readOnly}
          />
          
          {/* Packing tips - hide in read-only (share) view */}
          {!readOnly && (
            <Alert variant="info" className="mt-4">
              <Alert.Heading>{t('planDetail.packing.packingTips')}</Alert.Heading>
              <p>
                <strong>{t('planDetail.packing.rollDontFold')}</strong> {t('planDetail.packing.rollDontFoldDesc')}
              </p>
              <p>
                <strong>{t('planDetail.packing.packEssentials')}</strong> {t('planDetail.packing.packEssentialsDesc')}
              </p>
              <p>
                <strong>{t('planDetail.packing.checkWeather')}</strong> {t('planDetail.packing.checkWeatherDesc')}
              </p>
            </Alert>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default PackingTab; 